import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import EarningsPage from "@/pages/EarningsPageRebuild";
import CareerPage from "@/pages/CareerPageRebuild";
import MorePage from "@/pages/MorePage";
import UnderstandingYourPayPage from "@/pages/UnderstandingYourPayPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminHome from "@/pages/AdminHomeExact";
import AdminTeamPage from "@/pages/AdminTeamPage";
import TeamRecordsPage from "@/pages/TeamRecordsPage";
import AdminPayrollPage from "@/pages/AdminPayrollPage";
import HomebaseImportPage from "@/pages/HomebaseImportPage";
import BookerImportPage from "@/pages/BookerImportPage";
import ADPImportPage from "@/pages/ADPImportPage";
import HealthReimbursementAdmin from "@/pages/HealthReimbursementAdmin";
import ImportHubPage from "@/pages/ImportHubPage";
import TextImportPage from "@/pages/TextImportPage";
import NewPeriodPage from "@/pages/NewPeriodPage";
import ManagerDashboard from "@/pages/ManagerDashboard";
import ManagerApprovalsPage from "@/pages/ManagerApprovalsPage";
import PTOManagement from "@/pages/PTOManagement";
import AnnouncementsCenter from "@/pages/AnnouncementsCenter";
import DashboardLayout from "@/components/DashboardLayout";
import EmployeeHome from "@/pages/EmployeeHome";
import PaydayRecap from "@/pages/PaydayRecap";
import CheckIn from "@/pages/CheckIn";
import HealthReimbursement from "@/pages/HealthReimbursement";
import SettingsPage from "@/pages/SettingsPage";
import ProfilePage from "@/pages/ProfilePage";
import PayHistoryPage from "@/pages/PayHistoryPage";
import { CuteLoader, LoaderHost } from "@/components/CuteLoader";

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return <CuteLoader />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        {/* Home route based on role and active view */}
        <Route index element={
          (user?.role === "administrator" && user?.active_view !== "preview_employee" && user?.active_view !== "self_employee") ? (
            <AdminHome />
          ) : user?.role === "manager" ? (
            <ManagerDashboard />
          ) : (
            <EmployeeHome />
          )
        } />
        
        {/* Employee routes */}
        <Route path="earnings" element={
          <ProtectedRoute requiredRole="employee">
            <EarningsPage />
          </ProtectedRoute>
        } />
        <Route path="pto" element={
          <ProtectedRoute requiredRole="employee">
            <PTOManagement />
          </ProtectedRoute>
        } />
        <Route path="career" element={
          <ProtectedRoute requiredRole="employee">
            <CareerPage />
          </ProtectedRoute>
        } />
        <Route path="understanding-pay" element={<UnderstandingYourPayPage />} />
        <Route path="employee/home" element={
          <ProtectedRoute requiredRole="employee">
            <EmployeeHome />
          </ProtectedRoute>
        } />
        <Route path="employee/payday-recap/:periodId" element={
          <ProtectedRoute requiredRole="employee">
            <PaydayRecap />
          </ProtectedRoute>
        } />
        <Route path="employee/check-in" element={
          <ProtectedRoute requiredRole="employee">
            <CheckIn />
          </ProtectedRoute>
        } />
        
        {/* Manager routes */}
        <Route path="manager/team" element={
          <ProtectedRoute requiredRole="manager">
            <ManagerDashboard />
          </ProtectedRoute>
        } />
        <Route path="manager/approvals" element={
          <ProtectedRoute requiredRole="manager">
            <ManagerApprovalsPage />
          </ProtectedRoute>
        } />
        
        {/* Admin routes */}
        <Route path="admin" element={
          <ProtectedRoute requiredRole="administrator">
            <AdminHome />
          </ProtectedRoute>
        } />
        <Route path="admin/team" element={
          <ProtectedRoute requiredRole="administrator">
            <AdminTeamPage />
          </ProtectedRoute>
        } />
        <Route path="admin/users" element={
          <ProtectedRoute requiredRole="administrator">
            <AdminTeamPage />
          </ProtectedRoute>
        } />
        <Route path="admin/payroll" element={
          <ProtectedRoute requiredRole="administrator">
            <AdminPayrollPage />
          </ProtectedRoute>
        } />
        <Route path="admin/periods" element={
          <ProtectedRoute requiredRole="administrator">
            <AdminPayrollPage />
          </ProtectedRoute>
        } />
        <Route path="admin/team-records" element={
          <ProtectedRoute requiredRole="administrator">
            <TeamRecordsPage />
          </ProtectedRoute>
        } />
        <Route path="admin/pto" element={
          <ProtectedRoute requiredRole="administrator">
            <PTOManagement />
          </ProtectedRoute>
        } />
        <Route path="admin/health-reimbursement" element={
          <ProtectedRoute requiredRole="administrator">
            <HealthReimbursementAdmin />
          </ProtectedRoute>
        } />
        <Route path="admin/config" element={
          <ProtectedRoute requiredRole="administrator">
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="admin/service-catalog" element={
          <ProtectedRoute requiredRole="administrator">
            <HomebaseImportPage />
          </ProtectedRoute>
        } />
        <Route path="admin/import/booker" element={
          <ProtectedRoute requiredRole="administrator">
            <BookerImportPage />
          </ProtectedRoute>
        } />
        <Route path="admin/import/adp" element={
          <ProtectedRoute requiredRole="administrator">
            <ADPImportPage />
          </ProtectedRoute>
        } />
        <Route path="admin/import/hub" element={
          <ProtectedRoute requiredRole="administrator">
            <ImportHubPage />
          </ProtectedRoute>
        } />
        <Route path="admin/import/text" element={
          <ProtectedRoute requiredRole="manager">
            <TextImportPage />
          </ProtectedRoute>
        } />
        <Route path="admin/period/new" element={
          <ProtectedRoute requiredRole="administrator">
            <NewPeriodPage />
          </ProtectedRoute>
        } />
        
        {/* Shared routes */}
        <Route path="admin/announcements" element={<AnnouncementsCenter />} />
        <Route path="announcements" element={<AnnouncementsCenter />} />
        <Route path="more" element={<MorePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="pay-history" element={<PayHistoryPage />} />
        <Route path="health-reimbursement" element={<HealthReimbursement />} />
      </Route>
      <Route path="*" element={
        <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center p-6">
          <div className="text-center">
            <h1 className="text-2xl font-serif text-[#231E1A] mb-4">Page Not Found</h1>
            <p className="text-[#9C6B4A] mb-6">The route you requested does not exist</p>
            <button onClick={() => window.location.href = "/"} className="px-6 py-3 bg-[#9C6B4A] text-white rounded-lg">Go Home</button>
          </div>
        </div>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <LoaderHost />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
